import { Tooltip } from '@protoss/ui';
import * as React from 'react';
import { ConfirmDeleteDialog } from '../../../components/ConfirmDeleteDialog';
import { PageErrorState } from '../../../components/shared/PageErrorState';
import type { PlatformCatalogEntry, PlatformLifecycleState } from '../hooks/useProjectResources';
import { useProjectResources } from '../hooks/useProjectResources';

export interface ProjectResourcesPanelHandle {
  openAddPicker: () => void;
  reload: () => void;
}

interface ProjectResourcesPanelProps {
  projectUuid: string;
  canManage: boolean;
}

export const ProjectResourcesPanel = React.forwardRef<
  ProjectResourcesPanelHandle,
  ProjectResourcesPanelProps
>(function ProjectResourcesPanel({ projectUuid, canManage }, ref) {
  const { resources, loading, error, reload, deleteResource, addResource, getAvailablePlatforms } =
    useProjectResources(projectUuid);

  const [isAdding, setIsAdding] = React.useState(false);
  const [catalog, setCatalog] = React.useState<PlatformCatalogEntry[]>([]);
  const [catalogLoading, setCatalogLoading] = React.useState(false);
  const [removeTarget, setRemoveTarget] = React.useState<{
    platformKey: string;
    label: string;
  } | null>(null);
  const [removing, setRemoving] = React.useState(false);
  const [actionError, setActionError] = React.useState<string | null>(null);

  const openAddPicker = React.useCallback(async () => {
    setIsAdding(true);
    setCatalogLoading(true);
    setActionError(null);
    try {
      const entries = await getAvailablePlatforms();
      setCatalog(entries);
    } catch {
      setActionError('Failed to load platform catalog');
    } finally {
      setCatalogLoading(false);
    }
  }, [getAvailablePlatforms]);

  React.useImperativeHandle(ref, () => ({ openAddPicker, reload }), [openAddPicker, reload]);

  // Close add panel on Escape
  React.useEffect(() => {
    if (!isAdding) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setIsAdding(false);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [isAdding]);

  const handleRemove = async () => {
    if (!removeTarget || removing) return;
    setActionError(null);
    setRemoving(true);
    try {
      await deleteResource(removeTarget.platformKey);
      setRemoveTarget(null);
    } catch {
      setActionError('Failed to remove platform');
      setRemoveTarget(null);
    } finally {
      setRemoving(false);
    }
  };

  const handleAdd = async (key: string) => {
    setActionError(null);
    try {
      await addResource(key);
      setIsAdding(false);
      setCatalog([]);
    } catch {
      setActionError('Failed to add platform');
    }
  };

  if (loading) {
    return (
      <div className="res-panel">
        <div className="res-list__empty">Loading…</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="res-panel">
        <PageErrorState message={error} onRetry={() => void reload()} />
      </div>
    );
  }

  // Split detected resources into connectable platforms and informational technologies
  const platforms = resources.filter((r) => r.platformType === 'platform');
  const technologies = resources.filter((r) => r.platformType !== 'platform');

  // Group technologies by category for display
  const techGrouped = new Map<string, typeof technologies>();
  for (const resource of technologies) {
    const cat = resource.category ?? 'other';
    if (!techGrouped.has(cat)) techGrouped.set(cat, []);
    techGrouped.get(cat)!.push(resource);
  }

  return (
    <div className="res-panel">
      {actionError && (
        <div className="res-panel__error" role="alert">
          {actionError}
        </div>
      )}

      {/* Add platform picker */}
      {isAdding && (
        <div className="res-add-picker">
          <div className="res-add-picker__header">
            <span className="res-add-picker__title">Add platform</span>
            <button
              type="button"
              className="res-add-picker__close"
              onClick={() => setIsAdding(false)}
              aria-label="Close"
            >
              ×
            </button>
          </div>
          <div className="res-add-picker__list">
            {catalogLoading ? (
              <div className="res-add-picker__empty">Loading…</div>
            ) : catalog.length === 0 ? (
              <div className="res-add-picker__empty">All platforms already added</div>
            ) : (
              catalog.map((entry) => (
                <button
                  key={entry.key}
                  type="button"
                  className="res-add-picker__item"
                  onClick={() => handleAdd(entry.key)}
                >
                  <span className="res-add-picker__item-label">{entry.label}</span>
                  <span className="res-add-picker__item-category">
                    {CATEGORY_LABELS[entry.category] ?? entry.category}
                  </span>
                </button>
              ))
            )}
          </div>
        </div>
      )}

      {/* Empty state */}
      {resources.length === 0 && !isAdding ? (
        <div className="res-list__empty">
          <p className="res-list__empty-title">No resources detected</p>
          <p className="res-list__empty-body">
            Sync a repository to automatically detect platforms and frameworks.
          </p>
        </div>
      ) : (
        <div className="res-list">
          {/* ── Platforms section ─────────────────────────────────────────── */}
          {platforms.length > 0 && (
            <div className="res-list__section">
              <div className="res-list__section-label">Platforms</div>
              {platforms.map((resource) => (
                <div key={resource.platformKey} className="res-list__item res-list__item--platform">
                  <div className="res-list__item-body">
                    <span className="res-list__item-label">{resource.label}</span>
                    {resource.externalUrl ? (
                      <a
                        className="res-list__item-link"
                        href={resource.externalUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        aria-label={`${resource.label} documentation`}
                      >
                        Docs
                      </a>
                    ) : null}
                  </div>
                  {resource.lifecycleState && (
                    <div className="res-list__lifecycle">
                      <span
                        className={`res-list__lifecycle-badge res-list__lifecycle-badge--${resource.lifecycleState}`}
                      >
                        {LIFECYCLE_LABELS[resource.lifecycleState]}
                      </span>
                      {resource.lifecycleState !== 'configured' && (
                        <p className="res-list__lifecycle-hint">
                          {LIFECYCLE_HINTS[resource.lifecycleState]}
                        </p>
                      )}
                    </div>
                  )}
                  {canManage && (
                    <Tooltip.Simple label="Remove platform">
                      <button
                        type="button"
                        className="res-list__delete-btn"
                        onClick={(e) => {
                          e.stopPropagation();
                          setRemoveTarget({
                            platformKey: resource.platformKey,
                            label: resource.label,
                          });
                        }}
                        aria-label="Remove platform"
                      >
                        ×
                      </button>
                    </Tooltip.Simple>
                  )}
                </div>
              ))}
            </div>
          )}

          {/* ── Technologies section ───────────────────────────────────────── */}
          {techGrouped.size > 0 && (
            <div className="res-list__section">
              <div className="res-list__section-label">Technologies</div>
              {Array.from(techGrouped.entries()).map(([category, items]) => (
                <div key={category} className="res-list__group">
                  <div className="res-list__group-label">
                    {CATEGORY_LABELS[category] ?? category}
                  </div>
                  {items.map((resource) => (
                    <div key={resource.platformKey} className="res-list__item">
                      <div className="res-list__item-body">
                        <span className="res-list__item-label">{resource.label}</span>
                        {resource.externalUrl ? (
                          <a
                            className="res-list__item-link"
                            href={resource.externalUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                            aria-label={`${resource.label} documentation`}
                          >
                            Docs
                          </a>
                        ) : null}
                      </div>
                      {canManage && (
                        <Tooltip.Simple label="Remove technology">
                          <button
                            type="button"
                            className="res-list__delete-btn"
                            onClick={(e) => {
                              e.stopPropagation();
                              setRemoveTarget({
                                platformKey: resource.platformKey,
                                label: resource.label,
                              });
                            }}
                            aria-label="Remove technology"
                          >
                            ×
                          </button>
                        </Tooltip.Simple>
                      )}
                    </div>
                  ))}
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      <ConfirmDeleteDialog
        open={removeTarget !== null}
        onOpenChange={(open) => {
          if (!open && !removing) setRemoveTarget(null);
        }}
        title="Remove resource?"
        confirmKeyword={removeTarget?.label ?? ''}
        description="This resource will be removed from the project."
        confirmLabel="Remove"
        pendingLabel="Removing…"
        isPending={removing}
        onConfirm={handleRemove}
      />
    </div>
  );
});

// ── Category labels ───────────────────────────────────────────────────────────

export const CATEGORY_LABELS: Record<string, string> = {
  data_platform: 'Data Platforms',
  cloud: 'Cloud',
  language_runtime: 'Language Runtimes',
  iac: 'Infrastructure',
  container: 'Containers',
  framework: 'Frameworks',
  database: 'Databases',
  other: 'Other',
};

// ── Platform lifecycle labels & hints ─────────────────────────────────────────

export const LIFECYCLE_LABELS: Record<PlatformLifecycleState, string> = {
  no_integration_concept: 'Detected',
  detected_no_workspace_integration: 'Integration required',
  detected_workspace_integration_available: 'Ready to attach',
  configured: 'Connected',
};

export const LIFECYCLE_HINTS: Record<PlatformLifecycleState, string> = {
  no_integration_concept:
    'This platform was detected in the repository. No integration is required.',
  detected_no_workspace_integration:
    'This platform was detected. A workspace admin must create a workspace integration before it can be attached to this project.',
  detected_workspace_integration_available:
    'A workspace integration is available. A project admin can attach and configure it for this project.',
  configured: '',
};
